// Schedule logic shared by the hosted site and the widget: weeks in the
// studio's time zone, grouping by day, filters and the "places left" state.
import { addDays, format, isValid, parse, startOfWeek } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import type { Cancellation } from "./checkout";

export type SessionType = "COURSE" | "WORKSHOP" | "RETREAT";

/** One class in the public schedule (GET /public/studios/:slug/planning). */
export type ScheduleSession = {
  id: string;
  type: SessionType;
  title: string;
  startTime: string;
  endTime: string;
  teacherName: string | null;
  room: string | null;
  isOnline: boolean;
  /** null = no capacity limit. */
  spotsLeft: number | null;
  waitlistCount: number;
  waitlistEnabled: boolean;
  /** Drop-in price for classes, lowest option for workshops; null = passes only. */
  price: number | null;
  priceFrom: boolean;
  bookable: boolean;
  /** Name of the studio closure that cancels this date; null or absent when open. */
  closedFor?: string | null;
  /** Dot colour: the class type's, else the course's. */
  color?: string | null;
  classType?: ClassTypeRef | null;
  /** Free cancellation, computed by the API (older answers leave it out). */
  cancellation?: Cancellation | null;
};

/**
 * A closure banner at the top of the schedule (GET /public/studios/:slug/notices).
 * Days are "YYYY-MM-DD" in the studio's time zone, both included.
 */
export type ScheduleNotice = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  message: string | null;
};

/** The studio's class type ("Hatha", "Enfants"): dot colour and filter. */
export type ClassTypeRef = { id: string; name: string; color: string };

/** `type:<id>` filters on one class type. */
export type ScheduleFilter =
  | "all"
  | "studio"
  | "online"
  | "workshops"
  | `type:${string}`;

/** "Few places" threshold: shown in the warning colour. */
export const FEW_SPOTS = 2;

const DAY_KEY = "yyyy-MM-dd";

export type ScheduleWeek = {
  /** Monday of the week, "yyyy-MM-dd" in the studio time zone. */
  start: string;
  /** The seven day keys, Monday first. */
  days: string[];
  /** Week bounds as instants, for the API. */
  from: Date;
  to: Date;
  previous: string;
  next: string;
  isCurrent: boolean;
};

/** Studio-local day of an instant, "yyyy-MM-dd". */
export function dayKey(date: Date | string, timeZone: string): string {
  return formatInTimeZone(new Date(date), timeZone, DAY_KEY);
}

/** Noon of a day key in the studio time zone: safe to format as that day. */
export function dayKeyToDate(key: string, timeZone: string): Date {
  return fromZonedTime(`${key}T12:00:00`, timeZone);
}

/**
 * The week to show. `requested` is any "yyyy-MM-dd" (from the URL); invalid or
 * missing means the current week. Weeks start on Monday.
 */
export function scheduleWeek(
  requested: string | null | undefined,
  timeZone: string,
  now: Date = new Date(),
): ScheduleWeek {
  const today = dayKey(now, timeZone);
  const parsed = requested ? parse(requested, DAY_KEY, new Date(0)) : null;
  const anchor = parsed && isValid(parsed) ? parsed : parse(today, DAY_KEY, new Date(0));

  // Plain calendar arithmetic on local dates, then converted to instants.
  const monday = startOfWeek(anchor, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => format(addDays(monday, i), DAY_KEY));
  const start = days[0]!;
  const nextMonday = format(addDays(monday, 7), DAY_KEY);

  return {
    start,
    days,
    from: fromZonedTime(`${start}T00:00:00`, timeZone),
    to: fromZonedTime(`${nextMonday}T00:00:00`, timeZone),
    previous: format(addDays(monday, -7), DAY_KEY),
    next: nextMonday,
    isCurrent: days.includes(today),
  };
}

export function isWorkshop(session: ScheduleSession) {
  return session.type === "WORKSHOP" || session.type === "RETREAT";
}

export function matchesFilter(session: ScheduleSession, filter: ScheduleFilter) {
  if (filter.startsWith("type:")) {
    return session.classType?.id === filter.slice(5);
  }
  switch (filter) {
    case "studio":
      return !session.isOnline;
    case "online":
      return session.isOnline;
    case "workshops":
      return isWorkshop(session);
    default:
      return true;
  }
}

/** Class types present in these sessions, by name. */
export function classTypesOf(sessions: ScheduleSession[]): ClassTypeRef[] {
  const types = new Map<string, ClassTypeRef>();
  for (const s of sessions) {
    if (s.classType && !types.has(s.classType.id)) {
      types.set(s.classType.id, s.classType);
    }
  }
  return [...types.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Filters worth offering: only those that would change the list.
 * `classTypes`: one more filter per class type (the widget).
 */
export function availableFilters(
  sessions: ScheduleSession[],
  { classTypes = false }: { classTypes?: boolean } = {},
): ScheduleFilter[] {
  const candidates: ScheduleFilter[] = [
    "studio",
    "online",
    "workshops",
    ...(classTypes ? classTypesOf(sessions).map((t) => `type:${t.id}` as const) : []),
  ];
  const options = candidates.filter((f) => {
    const count = sessions.filter((s) => matchesFilter(s, f)).length;
    return count > 0 && count < sessions.length;
  });
  return options.length ? ["all", ...options] : [];
}

export type DayPeriod = "morning" | "afternoon" | "evening";

/** Morning before 12:00, afternoon before 17:00, evening after, studio time. */
export function dayPeriod(date: Date | string, timeZone: string): DayPeriod {
  const hour = Number(formatInTimeZone(new Date(date), timeZone, "H"));
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}

/** Started classes: shown greyed out, never bookable. */
export function hasStarted(session: ScheduleSession, now: Date = new Date()): boolean {
  return new Date(session.startTime) <= now;
}

export function groupByDay(
  sessions: ScheduleSession[],
  timeZone: string,
): Map<string, ScheduleSession[]> {
  const groups = new Map<string, ScheduleSession[]>();
  for (const session of sessions) {
    const key = dayKey(session.startTime, timeZone);
    groups.set(key, [...(groups.get(key) ?? []), session]);
  }
  return groups;
}

/**
 * What a day cell says (Run 4): the classes that still take place, or
 * "Closed" when the day has classes and the studio closure cancels all of
 * them. It said "1 class" before, for a class nobody could book.
 */
export function daySummary(sessions: ScheduleSession[] | undefined): { open: number; closed: boolean } {
  const list = sessions ?? [];
  const open = list.filter((s) => !s.closedFor).length;
  return { open, closed: list.length > 0 && open === 0 };
}

/** Day selected on load: the requested one, else the first day from today that has classes. */
export function initialDay(
  week: ScheduleWeek,
  byDay: Map<string, ScheduleSession[]>,
  timeZone: string,
  requested?: string | null,
  now: Date = new Date(),
): string {
  if (requested && week.days.includes(requested)) return requested;
  const today = dayKey(now, timeZone);
  const upcoming = week.days.filter((d) => d >= today);
  return (
    upcoming.find((d) => byDay.get(d)?.length) ??
    upcoming[0] ??
    week.days.find((d) => byDay.get(d)?.length) ??
    week.days[0]!
  );
}

export type SpotsState =
  | { kind: "open" }
  | { kind: "available"; count: number }
  | { kind: "few"; count: number }
  | { kind: "full"; waitlist: number; canJoinWaitlist: boolean };

export function spotsState(session: ScheduleSession): SpotsState {
  if (session.spotsLeft === null) return { kind: "open" };
  if (session.spotsLeft <= 0) {
    return {
      kind: "full",
      waitlist: session.waitlistCount,
      canJoinWaitlist: session.waitlistEnabled,
    };
  }
  return session.spotsLeft <= FEW_SPOTS
    ? { kind: "few", count: session.spotsLeft }
    : { kind: "available", count: session.spotsLeft };
}

export function durationMinutes(session: ScheduleSession): number {
  return Math.round(
    (new Date(session.endTime).getTime() - new Date(session.startTime).getTime()) / 60000,
  );
}

/**
 * Classes to suggest after a booking: upcoming, bookable, not full, not
 * already booked. The same class at another time comes first, then the
 * soonest ones.
 */
export function suggestNextClasses(
  sessions: ScheduleSession[],
  booked: { id: string; title: string },
  bookedIds: Iterable<string> = [],
  count = 3,
  now: Date = new Date(),
): ScheduleSession[] {
  const skip = new Set([booked.id, ...bookedIds]);
  return sessions
    .filter(
      (s) =>
        !skip.has(s.id) &&
        s.bookable &&
        new Date(s.startTime) > now &&
        spotsState(s).kind !== "full",
    )
    .sort(
      (a, b) =>
        Number(b.title === booked.title) - Number(a.title === booked.title) ||
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )
    .slice(0, count);
}
